/**
 * Serialización de query strings — reemplazo de jQuery `param(obj, true)`.
 *
 * ─── Por qué existe ──────────────────────────────────────────────────────────
 *
 * Los action creators construían sus URLs con la utilidad de jQuery, que era el
 * único motivo real para cargar jQuery en 29 archivos de `actions/`.
 *
 * ─── Por qué NO se usa URLSearchParams ───────────────────────────────────────
 *
 * `URLSearchParams` serializa según `application/x-www-form-urlencoded`, que
 * codifica el espacio como `+`. jQuery usa `encodeURIComponent`, que lo
 * codifica como `%20`:
 *
 *     jQuery      { q: 'juan perez' }  =>  'q=juan%20perez'
 *     URLSearchParams                  =>  'q=juan+perez'
 *
 * La mayoría de backends aceptan ambas, pero cambiar la codificación de todas
 * las peticiones del sistema "porque suele dar igual" es justo el tipo de
 * suposición que rompe un filtro de búsqueda en producción sin dejar rastro.
 * Este helper replica `encodeURIComponent`, así que el byte que llega al
 * backend es exactamente el mismo que antes.
 *
 * ─── Contrato (capturado empíricamente de jQuery 3.6) ────────────────────────
 *
 *   { page: 1 }                    => 'page=1'
 *   { ordering: '' }               => 'ordering='
 *   { created_at_0: null }         => 'created_at_0='      ← la clave se queda
 *   { x: undefined }               => 'x='                 ← la clave se queda
 *   { type: [] }                   => ''                   ← se omite entera
 *   { status: ['a','b'] }          => 'status=a&status=b'  ← clave repetida
 *   { q: 'juan perez' }            => 'q=juan%20perez'
 *   {}                             => ''
 */

/** Codifica igual que jQuery: `encodeURIComponent`, sin convertir espacios a `+`. */
const codificar = valor => encodeURIComponent(valor)

/**
 * Convierte un valor suelto a su representación de cadena.
 * `null` y `undefined` producen cadena vacía, no las palabras "null"/"undefined".
 */
const aCadena = valor =>
  valor === null || valor === undefined ? '' : String(valor)

/**
 * Serializa un objeto plano a query string, en modo *traditional*: los arrays
 * repiten la clave (`a=1&a=2`) en vez de usar corchetes (`a[]=1&a[]=2`).
 *
 * @param {object} params Objeto plano de parámetros.
 * @returns {string} Query string SIN el `?` inicial. Cadena vacía si no hay nada.
 */
export const buildQueryString = (params = {}) => {
  if (!params || typeof params !== 'object') return ''

  const partes = []

  Object.keys(params).forEach(clave => {
    const valor = params[clave]
    const claveCodificada = codificar(clave)

    if (Array.isArray(valor)) {
      // Un array vacío no aporta ningún par; el resto repite la clave.
      valor.forEach(elemento => {
        partes.push(`${claveCodificada}=${codificar(aCadena(elemento))}`)
      })
      return
    }

    partes.push(`${claveCodificada}=${codificar(aCadena(valor))}`)
  })

  return partes.join('&')
}

/**
 * Añade un query string a un endpoint, sólo si hay algo que añadir.
 *
 * Evita repetir en cada action el patrón de tres líneas que construía la URL a
 * mano comprobando si el query string quedaba vacío.
 *
 * @param {string} endpoint Ruta base, p. ej. `'orders/'`.
 * @param {object} params   Parámetros a serializar.
 * @returns {string} El endpoint con `?query` si procede.
 */
export const withQuery = (endpoint, params = {}) => {
  const qs = buildQueryString(params)
  return qs ? `${endpoint}?${qs}` : endpoint
}

export default buildQueryString
